import { HeadProps } from "gatsby"
import React from "react"

import Layout from "../../components/Layout"
import Section from "../../components/Section"
import SimpleHeader from "../../components/SimpleHeader"
import ResponsibleDisclosureSEO from "../../content/seo/responsible-disclosure"
import HeadParent from "../../utils/header"

function ResponsibleDisclosure() {
  return (
    <Layout navColor="bg-sand-600" navTextColor="dark">
      <SimpleHeader
        title="Responsible Disclosure & Bug Bounty Policy"
        lastUpdated="20 oktober 2025"
      />
      <Section bgColor="bg-sand" pt="pt-12 lg:pt-28" pb="pb-12 lg:pb-28">
        <div className="mx-auto max-w-3xl">
          <div className="text-plum space-y-4 text-base">
            <p>
              At Closure, the security of our systems and the privacy of our users are top priorities. We greatly appreciate the efforts of ethical hackers and security researchers who help us keep our platform safe.
            </p>
            <p>
              No matter how much care we take to secure our systems, vulnerabilities may still exist. If you discover one, we ask that you inform us as soon as possible so we can take appropriate action. Please follow the guidelines below to help us protect our users and systems responsibly.
            </p>
            <p>
              For more information about our security practices, please visit our{' '}
              <a href="https://trust.closure.nl" target="_blank" rel="noopener noreferrer" className="underline">
                Trust Platform
              </a>
              .
            </p>

            <h1 id="how-to-report" className="text-plum text-lg mt-8">
              <strong>How to Report a Vulnerability</strong>
            </h1>
            <p>
              Please report any suspected vulnerabilities by email to:
              <br />
              📧 <a href="mailto:security@closure.nl" className="underline">security@closure.nl</a>
            </p>
            <p>When submitting your report, please include:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>A detailed description of the vulnerability</li>
              <li>The URL, IP address, or system affected</li>
              <li>Steps to reproduce the issue (proof-of-concept preferred)</li>
              <li>Any relevant technical details, screenshots, or logs</li>
            </ul>
            <p>
              We ask that you do not publicly disclose the issue until it has been resolved.
            </p>

            <h1 id="dos" className="text-plum text-lg mt-8">
              <strong>Do's</strong>
            </h1>
            <ul className="list-disc pl-6 space-y-2">
              <li>Report the vulnerability as quickly as reasonably possible.</li>
              <li>Report in a confidential manner, ensuring the information is not shared publicly before resolution.</li>
              <li>Provide enough detail for us to reproduce and assess the issue.</li>
              <li>Use test accounts and sample data only when conducting research.</li>
            </ul>

            <h1 id="donts" className="text-plum text-lg mt-8">
              <strong>Don'ts</strong>
            </h1>
            <ul className="list-disc pl-6 space-y-2">
              <li>Do not share details of the vulnerability with others before it has been resolved.</li>
              <li>Do not access, copy, modify, or delete data.</li>
              <li>Do not exploit the vulnerability beyond what is necessary to prove its existence.</li>
              <li>
                Do not perform actions that could disrupt our services, such as:
                <ul className="list-disc pl-6 mt-2 space-y-1">
                  <li>Brute-force attacks</li>
                  <li>Automated or mass submissions to forms or scripted POST scraping</li>
                  <li>Denial of Service (DoS)</li>
                  <li>Social engineering</li>
                  <li>Spam or phishing</li>
                  <li>Physical security attacks</li>
                </ul>
              </li>
            </ul>

            <h1 id="what-to-expect" className="text-plum text-lg mt-8">
              <strong>What You Can Expect from Us</strong>
            </h1>
            <ul className="list-disc pl-6 space-y-2">
              <li>We will acknowledge your report within 5 business days.</li>
              <li>We will keep you informed of our progress as we resolve the issue.</li>
              <li>If you follow the guidelines in this policy, we will not take legal action against you for your responsible disclosure.</li>
            </ul>

            <h1 id="bug-bounty" className="text-plum text-lg mt-8">
              <strong>Bug Bounty</strong>
            </h1>
            <p>
              Closure appreciates your help in keeping our systems secure. Depending on the severity and impact of the reported vulnerability, we may offer a monetary reward (bug bounty) as a token of appreciation.
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Typical rewards range from €30 for low-severity issues up to €300 for critical vulnerabilities.</li>
              <li>The specific reward amount is determined at our discretion based on the impact, exploitability, and quality of the report.</li>
            </ul>
            <p>We will not issue a bounty for vulnerabilities that:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Were found through non-compliant or unethical testing methods</li>
              <li>Are already known to us</li>
              <li>Cannot be proven to be exploitable</li>
              <li>Are unconfirmed reports from automated scanners</li>
              <li>Are related to rate limits, brute-force, or version disclosure ("banner grabbing")</li>
              <li>Affect systems or services that are out of scope</li>
            </ul>

            <h1 id="out-of-scope" className="text-plum text-lg mt-8">
              <strong>Out of Scope</strong>
            </h1>
            <p>The following are out of scope and not eligible for a bounty:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Third-party platforms or systems not operated by Closure BV</li>
              <li>Marketing or informational websites without user data</li>
              <li>Contact forms, chatbots, or other communication channels towards our Support team</li>
              <li>Denial of Service (DoS) or spam-related vulnerabilities</li>
              <li>Physical security vulnerabilities</li>
              <li>Issues requiring access to our internal corporate network</li>
            </ul>

            <h1 id="legal-safe-harbor" className="text-plum text-lg mt-8">
              <strong>Legal Safe Harbor</strong>
            </h1>
            <p>If you act in good faith and follow this policy:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Your actions will be considered authorized under applicable law.</li>
              <li>Closure BV will not pursue legal action related to your report.</li>
              <li>We welcome your contribution to improving the security of our systems.</li>
            </ul>

            <p className="mt-8">
              <strong>Thank you for helping us keep Closure secure.</strong>
              <br />
              You can find more about our security and compliance program at{' '}
              <a href="https://trust.closure.nl" target="_blank" rel="noopener noreferrer" className="underline">
                trust.closure.nl
              </a>
            </p>
          </div>
        </div>
      </Section>
    </Layout>
  )
}

export default ResponsibleDisclosure

export function Head(props: HeadProps) {
  const { location } = props
  return (
    <HeadParent pageTitle="Responsible Disclosure" location={location}>
      <ResponsibleDisclosureSEO />
    </HeadParent>
  )
}

